package pl.nickers.match3.ecs

import pl.nickers.match3.BombPowerUpFlightUi
import pl.nickers.match3.DebugInfo
import pl.nickers.match3.FamiliarFlightUi
import pl.nickers.match3.FamiliarInfo
import pl.nickers.match3.FallAnimationInfo
import pl.nickers.match3.FallingCells
import pl.nickers.match3.GameState
import pl.nickers.match3.GoalInfo
import pl.nickers.match3.GoalJellyFlightUi
import pl.nickers.match3.GridPos
import pl.nickers.match3.JellyCell
import pl.nickers.match3.MoveLimitInfo
import pl.nickers.match3.PowerUpInfo
import pl.nickers.match3.ScreenTransformInfo
import pl.nickers.match3.SwapAnimation
import pl.nickers.match3.SwirlVacuumInfo
import pl.nickers.match3.TeleportAnimationInfo
import pl.nickers.match3.TeleportLinkUi
import pl.nickers.match3.TeleportingCells
import pl.nickers.match3.TutorialOverlayUi

internal class RenderSystem(
    private val catalog: EntityCatalog = EntityCatalog.default(),
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var typeMapper: ComponentMapper<JellyTypeComponent>
    private lateinit var bodyImageMapper: ComponentMapper<BodyImageComponent>
    private lateinit var jellyFaceMapper: ComponentMapper<JellyFaceComponent>
    private lateinit var bombFaceMapper: ComponentMapper<BombFaceComponent>
    private lateinit var swappingMapper: ComponentMapper<SwappingComponent>
    private lateinit var fallingMapper: ComponentMapper<FallingComponent>
    private lateinit var iceCubeMapper: ComponentMapper<IceCubeComponent>
    private lateinit var explodingMapper: ComponentMapper<ExplodingComponent>
    private lateinit var fullBoardMapper: ComponentMapper<FullBoardExplosionComponent>
    private lateinit var fusedBombMapper: ComponentMapper<FusedBombComponent>
    private lateinit var activatedLaserMapper: ComponentMapper<ActivatedLaserComponent>
    private lateinit var draggableMapper: ComponentMapper<DraggableComponent>
    private lateinit var ghostBombMapper: ComponentMapper<GhostBombComponent>
    private lateinit var goalsMapper: ComponentMapper<GoalsComponent>
    private lateinit var moveLimitMapper: ComponentMapper<MoveLimitComponent>
    private lateinit var backgroundImageMapper: ComponentMapper<BackgroundImageComponent>
    private lateinit var powerUpTypeMapper: ComponentMapper<PowerUpTypeComponent>
    private lateinit var powerUpUsesMapper: ComponentMapper<PowerUpUsesComponent>
    private lateinit var flyingSpriteMapper: ComponentMapper<FlyingSpriteComponent>
    private lateinit var familiarFlightMapper: ComponentMapper<FamiliarFlightComponent>
    private lateinit var bombFlightMapper: ComponentMapper<BombFlightComponent>
    private lateinit var screenTransformMapper: ComponentMapper<ScreenTransformComponent>
    private lateinit var spawnPointMapper: ComponentMapper<SpawnPointComponent>
    private lateinit var swirlVacuumMapper: ComponentMapper<SwirlVacuumComponent>
    private lateinit var teleportingMapper: ComponentMapper<TeleportingComponent>
    private lateinit var teleportTargetMapper: ComponentMapper<TeleportTargetMarkerComponent>
    private lateinit var familiarMapper: ComponentMapper<FamiliarComponent>
    private lateinit var cursedMapper: ComponentMapper<CursedComponent>
    private lateinit var tutorialMapper: ComponentMapper<TutorialComponent>
    var gameState: GameState = GameState(grid = emptyList())
        private set

    override fun initialize() {
        posMapper = world.mapper()
        typeMapper = world.mapper()
        bodyImageMapper = world.mapper()
        jellyFaceMapper = world.mapper()
        bombFaceMapper = world.mapper()
        swappingMapper = world.mapper()
        fallingMapper = world.mapper()
        iceCubeMapper = world.mapper()
        explodingMapper = world.mapper()
        fullBoardMapper = world.mapper()
        fusedBombMapper = world.mapper()
        activatedLaserMapper = world.mapper()
        draggableMapper = world.mapper()
        ghostBombMapper = world.mapper()
        goalsMapper = world.mapper()
        moveLimitMapper = world.mapper()
        backgroundImageMapper = world.mapper()
        powerUpTypeMapper = world.mapper()
        powerUpUsesMapper = world.mapper()
        flyingSpriteMapper = world.mapper()
        familiarFlightMapper = world.mapper()
        bombFlightMapper = world.mapper()
        screenTransformMapper = world.mapper()
        spawnPointMapper = world.mapper()
        swirlVacuumMapper = world.mapper()
        teleportingMapper = world.mapper()
        teleportTargetMapper = world.mapper()
        familiarMapper = world.mapper()
        cursedMapper = world.mapper()
        tutorialMapper = world.mapper()
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        val gridWidth = board.width
        val gridHeight = board.height
        val aspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class)
        val entities = world.getEntitiesForAspect(aspect)
        val grid = Array(gridHeight) { arrayOfNulls<JellyCell>(gridWidth) }
        // E5 terrain tiles are separate entities (no JellyTypeComponent); project them
        // onto per-cell flags so both occupied and empty cells render their floor.
        val voidPuddleCells = world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, VoidPuddleTerrainComponent::class),
        ).mapNotNullTo(mutableSetOf()) { posMapper[it]?.let { p -> p.row to p.col } }
        val purifiedRuneCells = world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, PurifiedRuneComponent::class),
        ).mapNotNullTo(mutableSetOf()) { posMapper[it]?.let { p -> p.row to p.col } }
        entities.forEach { entityId ->
            val pos = posMapper[entityId]!!
            val type = typeMapper[entityId]!!.type
            val family = world.familyOf(entityId, pieceSet)
            val isCrossBomb = family == Family.CROSS_BOMB
            // The rendered "is a bomb" flag is true for both plain bombs and cross-bombs,
            // since the cross-bomb body sits on top of the bomb body in the sprite stack.
            val isBomb = family == Family.BOMB || isCrossBomb
            val laserDir = world.laserDirectionOf(entityId, pieceSet)
            val isLaser = laserDir != null
            val isColourfulSwirl = family == Family.SWIRL
            val isIceCube = iceCubeMapper.has(entityId)
            val iceCube = iceCubeMapper[entityId]
            val isFused = fusedBombMapper.has(entityId)
            val spawnPoint = spawnPointMapper[entityId]
            val isSpawnPoint = spawnPoint != null
            val bodyImage = when {
                isIceCube && iceCube != null -> "ice_${iceCube.life.coerceIn(1, 3)}.png"
                isBomb -> bodyImageMapper[entityId]?.image ?: "bomb.png"
                else -> bodyImageMapper[entityId]?.image ?: "jelly_$type.png"
            }
            val faceImage = when {
                isIceCube -> "face_1.png"
                isBomb -> bombFaceMapper[entityId]?.image ?: "bomb_face_1.png"
                else -> jellyFaceMapper[entityId]?.image ?: "face_1.png"
            }
            val iceCubeLife = if (isIceCube && iceCube != null) iceCube.life else 0
            val st = screenTransformMapper[entityId]
            val screenTransform = if (st != null) {
                ScreenTransformInfo(
                    offsetX = st.offsetX,
                    offsetY = st.offsetY,
                    scale = st.scale,
                    alpha = st.alpha,
                )
            } else {
                ScreenTransformInfo()
            }
            if (pos.row in 0 until gridHeight && pos.col in 0 until gridWidth) {
                grid[pos.row][pos.col] = JellyCell(
                    type = type,
                    id = entityId,
                    isBomb = isBomb,
                    isCrossBomb = isCrossBomb,
                    isLaser = isLaser,
                    laserDirection = laserDir,
                    isColourfulSwirl = isColourfulSwirl,
                    isIceCube = isIceCube,
                    isEmpty = false,
                    iceCubeLife = iceCubeLife,
                    bodyImage = bodyImage,
                    faceImage = faceImage,
                    isDraggable = draggableMapper.has(entityId),
                    isFused = isFused,
                    isSpawnPoint = isSpawnPoint,
                    spawnPointVisible = spawnPoint?.visible ?: true,
                    isCursed = cursedMapper.has(entityId),
                    isVoidPuddle = (pos.row to pos.col) in voidPuddleCells,
                    isPurifiedRune = (pos.row to pos.col) in purifiedRuneCells,
                    screenTransform = screenTransform,
                )
            }
        }
        val gridList = grid.mapIndexed { rowIdx, row ->
            row.mapIndexed { colIdx, cell ->
                cell ?: JellyCell(
                    type = 0,
                    id = -1,
                    isEmpty = true,
                    isHole = world.isHole(rowIdx, colIdx),
                    isVoidPuddle = (rowIdx to colIdx) in voidPuddleCells,
                    isPurifiedRune = (rowIdx to colIdx) in purifiedRuneCells,
                )
            }
        }
        val selectedEntity = world.getEntitiesForAspect(Aspect.all(SelectedComponent::class)).firstOrNull()
        val selected = selectedEntity?.let { posMapper[it] }?.let { GridPos(it.row, it.col) }
            ?: board.emptyCellSelection?.let { GridPos(it.first, it.second) }
        val swappingCells = buildMap {
            world.getEntitiesForAspect(Aspect.all(SwappingComponent::class)).forEach { entityId ->
                val swapping = swappingMapper[entityId]!!
                put(
                    entityId,
                    SwapAnimation(
                        from = GridPos(swapping.sourceRow, swapping.sourceCol),
                        to = GridPos(swapping.targetRow, swapping.targetCol),
                        isReturning = swapping.isReturning,
                    ),
                )
            }
        }
        val fallingEntities = world.getEntitiesForAspect(Aspect.all(FallingComponent::class))
        val fallingCells: FallingCells = buildMap {
            fallingEntities.forEach { entityId ->
                val falling = fallingMapper[entityId]!!
                val pos = posMapper[entityId]
                val gridCol = pos?.col ?: 0
                put(
                    entityId,
                    FallAnimationInfo(
                        fromRow = falling.fromRow,
                        toRow = falling.toRow,
                        fromCol = if (falling.fromCol >= 0) falling.fromCol else gridCol,
                        toCol = if (falling.toCol >= 0) falling.toCol else gridCol,
                    ),
                )
            }
        }
        val explodingEntities = world.getEntitiesForAspect(Aspect.all(ExplodingComponent::class))
        val activatedLaserEntities = world.getEntitiesForAspect(Aspect.all(ActivatedLaserComponent::class))
        val explodingBombs = explodingEntities.mapNotNull { entityId ->
            val ghostComp = ghostBombMapper[entityId]
            if (ghostComp != null) GridPos(ghostComp.row, ghostComp.col)
            else posMapper[entityId]?.let { GridPos(it.row, it.col) }
        }
        val fullBoardExplosion = explodingEntities.any { fullBoardMapper.has(it) }
        val fusedEntities = world.getEntitiesForAspect(Aspect.all(FusedBombComponent::class))
        val activatingLasers = activatedLaserEntities.flatMap { entityId ->
            val pos = posMapper[entityId] ?: return@flatMap emptyList()
            val defaultDir = world.laserDirectionOf(entityId, pieceSet) ?: return@flatMap emptyList()
            val activation = activatedLaserMapper[entityId]
            val directions = activation?.directions ?: setOf(defaultDir)
            directions.map { direction ->
                pl.nickers.match3.LaserAnimation(
                    origin = GridPos(pos.row, pos.col),
                    direction = direction,
                )
            }
        }
        val fusedBombs = fusedEntities.mapNotNull { entityId ->
            val ghostComp = ghostBombMapper[entityId]
            if (ghostComp != null) GridPos(ghostComp.row, ghostComp.col)
            else posMapper[entityId]?.let { GridPos(it.row, it.col) }
        }
        val swappingEntities = world.getEntitiesForAspect(Aspect.all(SwappingComponent::class))
        val debugInfo = DebugInfo(
            phase = board.phase.name,
            activatingLaserCount = activatedLaserEntities.count(),
            explodingBombCount = explodingEntities.count(),
            fusedBombCount = fusedEntities.count(),
            fallingCount = fallingEntities.count(),
            swappingCount = swappingEntities.count(),
        )
        val goalsInfo = world.boardEntityId()?.let { boardId ->
            val goalsComp = goalsMapper[boardId]
            goalsComp?.goals?.map { goal ->
                val bodyImage = pieceSet.pieces[goal.pieceId]?.sprites?.body ?: "jelly_1.png"
                GoalInfo(
                    pieceId = goal.pieceId,
                    bodyImage = bodyImage,
                    remainingQuantity = goal.remainingQuantity,
                    targetQuantity = goal.targetQuantity,
                    completed = goal.completed,
                )
            } ?: emptyList()
        } ?: emptyList()
        val moveLimitComp = world.boardEntityId()?.let { moveLimitMapper[it] }
        val moveLimit = moveLimitComp?.let { MoveLimitInfo(remaining = it.remainingMoves, total = it.totalMoves) }
        val lost = moveLimitComp != null && moveLimitComp.remainingMoves <= 0 && !board.won

        val backgroundImageBase64 = world.getEntitiesForAspect(
            Aspect.all(BackgroundImageComponent::class),
        ).firstOrNull()?.let { backgroundImageMapper[it]?.imageBase64 }

        val powerUps = world.getEntitiesForAspect(
            Aspect.all(PowerUpTypeComponent::class, PowerUpUsesComponent::class),
        ).map { entityId ->
            PowerUpInfo(
                type = powerUpTypeMapper[entityId]!!.type,
                uses = powerUpUsesMapper[entityId]!!.uses,
                entityId = entityId,
            )
        }

        val familiarEntity = world.getEntitiesForAspect(Aspect.all(FamiliarComponent::class)).firstOrNull()
        val familiarInfo = familiarEntity?.let {
            val fc = familiarMapper[it]
            fc?.let { c -> FamiliarInfo(progress = c.progress, threshold = c.threshold) }
        }

        val goalJellyFlights = world.getEntitiesForAspect(Aspect.all(FlyingSpriteComponent::class))
            .map { entityId ->
                val fs = flyingSpriteMapper[entityId]!!
                GoalJellyFlightUi(
                    entityId = entityId,
                    row = fs.row,
                    col = fs.col,
                    bodyImage = fs.bodyImage,
                    faceImage = fs.faceImage,
                    goalPieceId = fs.goalPieceId,
                )
            }

        val familiarFlights = world.getEntitiesForAspect(Aspect.all(FamiliarFlightComponent::class))
            .map { entityId ->
                val ff = familiarFlightMapper[entityId]!!
                FamiliarFlightUi(
                    entityId = entityId,
                    row = ff.row,
                    col = ff.col,
                    bodyImage = ff.bodyImage,
                    faceImage = ff.faceImage,
                )
            }

        val swirlVacuumCells = buildMap<Int, SwirlVacuumInfo> {
            world.getEntitiesForAspect(Aspect.all(SwirlVacuumComponent::class)).forEach { entityId ->
                val vac = swirlVacuumMapper[entityId] ?: return@forEach
                val pos = posMapper[entityId] ?: return@forEach
                put(entityId, SwirlVacuumInfo(
                    fromRow = pos.row,
                    fromCol = pos.col,
                    targetRow = vac.targetRow,
                    targetCol = vac.targetCol,
                ))
            }
        }

        val teleportingCells: TeleportingCells = buildMap {
            world.getEntitiesForAspect(Aspect.all(TeleportingComponent::class)).forEach { entityId ->
                val tp = teleportingMapper[entityId] ?: return@forEach
                put(
                    entityId,
                    TeleportAnimationInfo(
                        sourceRow = tp.sourceRow, sourceCol = tp.sourceCol,
                        destRow = tp.destRow, destCol = tp.destCol,
                    ),
                )
            }
        }

        val teleportLinks: List<TeleportLinkUi> = world
            .getEntitiesForAspect(Aspect.all(GridPositionComponent::class, TeleportTargetMarkerComponent::class))
            .mapNotNull { entityId ->
                val pos = posMapper[entityId] ?: return@mapNotNull null
                val marker = teleportTargetMapper[entityId] ?: return@mapNotNull null
                TeleportLinkUi(
                    source = GridPos(marker.sourceRow, marker.sourceCol),
                    destination = GridPos(pos.row, pos.col),
                )
            }

        val bombFlightEntity = world.getEntitiesForAspect(Aspect.all(BombFlightComponent::class)).firstOrNull()
        val bombPowerUpFlight = bombFlightEntity?.let { entityId ->
            val bf = bombFlightMapper[entityId]!!
            BombPowerUpFlightUi(
                entityId = entityId,
                targetRow = bf.targetRow,
                targetCol = bf.targetCol,
                powerUpEntityId = bf.powerUpEntityId,
            )
        }

        val tutorialUi = world.boardEntityId()?.let { tutorialMapper[it] }?.let { tutorial ->
            when (tutorial.step) {
                TutorialStep.SWAP_HINT -> world.firstAvailableSwapHint(gridWidth, gridHeight, pieceSet)
                    ?.let { (from, to) ->
                        TutorialOverlayUi(
                            step = TutorialStep.SWAP_HINT,
                            message = "Swap adjacent ingredients to brew a match of 3",
                            highlightFrom = GridPos(from.first, from.second),
                            highlightTo = GridPos(to.first, to.second),
                        )
                    }
                TutorialStep.POINT_AT_GOALS -> TutorialOverlayUi(
                    step = TutorialStep.POINT_AT_GOALS,
                    message = "Collect the ingredients shown above before you run out of moves",
                )
            }
        }

        gameState = GameState(
            grid = gridList,
            width = gridWidth,
            height = gridHeight,
            selected = selected,
            swappingCells = swappingCells,
            score = board.score,
            disableNewJellySpawn = world.boardSpawnControl()?.disableNewJellySpawn == true,
            fallingCells = fallingCells,
            activatingLasers = activatingLasers,
            explodingBombs = explodingBombs,
            fullBoardExplosion = fullBoardExplosion,
            fusedBombs = fusedBombs,
            debugInfo = debugInfo,
            goals = goalsInfo,
            moveLimit = moveLimit,
            won = board.won,
            lost = lost,
            backgroundImageBase64 = backgroundImageBase64,
            powerUps = powerUps,
            bombPowerUpFlight = bombPowerUpFlight,
            goalJellyFlights = goalJellyFlights,
            familiarFlights = familiarFlights,
            swirlVacuumCells = swirlVacuumCells,
            teleportingCells = teleportingCells,
            teleportLinks = teleportLinks,
            familiar = familiarInfo,
            tutorial = tutorialUi,
        )
    }
}
